import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Users, FileText, Image, LogOut, Trash2, Mail, Phone, Calendar, Download } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LoginForm({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data.success) {
        onLogin(password);
      }
    } catch {
      setError('Mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Lock className="w-12 h-12 text-gold-muted mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-alpine-mist">Administration</h1>
          <p className="text-alpine-mist/40 text-sm mt-2">Chalets Gemini 1308</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mot de passe"
            className="input-luxury w-full"
            data-testid="admin-password-input"
          />
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="cta-luxury w-full text-center"
            data-testid="admin-login-btn"
          >
            {loading ? '...' : 'Connexion'}
          </button>
        </form>
      </div>
    </div>
  );
}

function StatsCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white/5 border border-white/10 p-6 rounded">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded ${color}`}>
          <Icon size={20} className="text-white" />
        </div>
        <div>
          <p className="text-2xl font-serif text-alpine-mist">{value}</p>
          <p className="text-xs text-alpine-mist/40 uppercase tracking-wider">{label}</p>
        </div>
      </div>
    </div>
  );
}

function Dashboard({ password, onLogout }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({ contacts: 0, brochure_requests: 0, gallery_images: 0 });
  const [contacts, setContacts] = useState([]);
  const [brochureRequests, setBrochureRequests] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, contactsRes, brochureRes, galleryRes] = await Promise.all([
        axios.post(`${API}/admin/stats`, { password }),
        axios.post(`${API}/admin/contacts`, { password }),
        axios.post(`${API}/admin/brochure-requests`, { password }),
        axios.post(`${API}/admin/gallery`, { password })
      ]);
      setStats(statsRes.data);
      setContacts(contactsRes.data.contacts);
      setBrochureRequests(brochureRes.data.requests);
      setGallery(galleryRes.data.images);
    } catch (err) {
      console.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Supprimer ce contact ?')) return;
    try {
      await axios.delete(`${API}/admin/contact/${id}`, { data: { password } });
      setContacts(contacts.filter(c => c.id !== id));
      setStats(prev => ({ ...prev, contacts: prev.contacts - 1 }));
    } catch {
      alert('Erreur lors de la suppression');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: Users },
    { id: 'contacts', label: 'Contacts', icon: Mail },
    { id: 'brochures', label: 'Brochures', icon: FileText },
    { id: 'gallery', label: 'Galerie', icon: Image },
  ];

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Header */}
      <header className="border-b border-white/10 bg-obsidian/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-serif text-xl text-alpine-mist">Gemini 1308</span>
            <span className="text-xs text-gold-muted/60 uppercase tracking-wider">Admin</span>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 text-alpine-mist/40 hover:text-alpine-mist transition-colors text-sm"
            data-testid="admin-logout-btn"
          >
            <LogOut size={16} />
            Déconnexion
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-white/10 pb-4">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded text-sm transition-colors ${
                activeTab === tab.id 
                  ? 'bg-gold-muted/20 text-gold-muted' 
                  : 'text-alpine-mist/40 hover:text-alpine-mist'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-gold-muted/30 border-t-gold-muted rounded-full mx-auto" />
          </div>
        ) : (
          <>
            {/* Overview */}
            {activeTab === 'overview' && (
              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <StatsCard icon={Mail} label="Demandes de contact" value={stats.contacts} color="bg-blue-600" />
                  <StatsCard icon={Download} label="Téléchargements brochure" value={stats.brochure_requests} color="bg-green-600" />
                  <StatsCard icon={Image} label="Images galerie" value={stats.gallery_images} color="bg-purple-600" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Recent Contacts */}
                  <div className="bg-white/5 border border-white/10 rounded p-6">
                    <h3 className="text-alpine-mist font-medium mb-4">Derniers contacts</h3>
                    {contacts.slice(0, 5).map(c => (
                      <div key={c.id} className="border-b border-white/5 py-3 last:border-0">
                        <p className="text-sm text-alpine-mist">{c.name}</p>
                        <p className="text-xs text-alpine-mist/40">{c.email} • {formatDate(c.timestamp)}</p>
                      </div>
                    ))}
                    {contacts.length === 0 && <p className="text-alpine-mist/30 text-sm">Aucun contact</p>}
                  </div>

                  {/* Recent Brochure Requests */}
                  <div className="bg-white/5 border border-white/10 rounded p-6">
                    <h3 className="text-alpine-mist font-medium mb-4">Dernières demandes de brochure</h3>
                    {brochureRequests.slice(0, 5).map(r => (
                      <div key={r.id} className="border-b border-white/5 py-3 last:border-0">
                        <p className="text-sm text-alpine-mist">{r.email}</p>
                        <p className="text-xs text-alpine-mist/40">{r.name || '-'} • {formatDate(r.timestamp)}</p>
                      </div>
                    ))}
                    {brochureRequests.length === 0 && <p className="text-alpine-mist/30 text-sm">Aucune demande</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Contacts List */}
            {activeTab === 'contacts' && (
              <div className="bg-white/5 border border-white/10 rounded overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="text-left text-xs text-alpine-mist/50 uppercase tracking-wider px-6 py-4">Nom</th>
                        <th className="text-left text-xs text-alpine-mist/50 uppercase tracking-wider px-6 py-4">Email</th>
                        <th className="text-left text-xs text-alpine-mist/50 uppercase tracking-wider px-6 py-4">Téléphone</th>
                        <th className="text-left text-xs text-alpine-mist/50 uppercase tracking-wider px-6 py-4">Message</th>
                        <th className="text-left text-xs text-alpine-mist/50 uppercase tracking-wider px-6 py-4">Date</th>
                        <th className="text-left text-xs text-alpine-mist/50 uppercase tracking-wider px-6 py-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.map(c => (
                        <tr key={c.id} className="border-t border-white/5 hover:bg-white/5">
                          <td className="px-6 py-4 text-sm text-alpine-mist">{c.name}</td>
                          <td className="px-6 py-4 text-sm text-alpine-mist/70">
                            <a href={`mailto:${c.email}`} className="hover:text-gold-muted">{c.email}</a>
                          </td>
                          <td className="px-6 py-4 text-sm text-alpine-mist/70">
                            {c.phone ? <a href={`tel:${c.phone}`} className="hover:text-gold-muted">{c.phone}</a> : '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-alpine-mist/50 max-w-xs truncate">{c.message || '-'}</td>
                          <td className="px-6 py-4 text-sm text-alpine-mist/40">{formatDate(c.timestamp)}</td>
                          <td className="px-6 py-4">
                            <button 
                              onClick={() => deleteContact(c.id)}
                              className="text-red-400/50 hover:text-red-400 transition-colors"
                              data-testid={`delete-contact-${c.id}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {contacts.length === 0 && (
                    <p className="text-center py-12 text-alpine-mist/30">Aucun contact enregistré</p>
                  )}
                </div>
              </div>
            )}

            {/* Brochure Requests List */}
            {activeTab === 'brochures' && (
              <div className="bg-white/5 border border-white/10 rounded overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="text-left text-xs text-alpine-mist/50 uppercase tracking-wider px-6 py-4">Nom</th>
                        <th className="text-left text-xs text-alpine-mist/50 uppercase tracking-wider px-6 py-4">Email</th>
                        <th className="text-left text-xs text-alpine-mist/50 uppercase tracking-wider px-6 py-4">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brochureRequests.map(r => (
                        <tr key={r.id} className="border-t border-white/5 hover:bg-white/5">
                          <td className="px-6 py-4 text-sm text-alpine-mist">{r.name || '-'}</td>
                          <td className="px-6 py-4 text-sm text-alpine-mist/70">
                            <a href={`mailto:${r.email}`} className="hover:text-gold-muted">{r.email}</a>
                          </td>
                          <td className="px-6 py-4 text-sm text-alpine-mist/40">{formatDate(r.timestamp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {brochureRequests.length === 0 && (
                    <p className="text-center py-12 text-alpine-mist/30">Aucune demande de brochure</p>
                  )}
                </div>
              </div>
            )}

            {/* Gallery */}
            {activeTab === 'gallery' && (
              <div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {gallery.map(img => (
                    <div key={img.id} className="relative group overflow-hidden rounded">
                      <img 
                        src={img.src} 
                        alt={img.alt_fr} 
                        className="w-full h-40 object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                        <div>
                          <p className="text-xs text-white/90">{img.alt_fr}</p>
                          <span className={`inline-block mt-1 text-[10px] uppercase px-2 py-0.5 rounded ${
                            img.category === 'exterior' ? 'bg-blue-500/30 text-blue-300' : 'bg-purple-500/30 text-purple-300'
                          }`}>
                            {img.category}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-alpine-mist/30 text-xs mt-6 text-center">
                  {gallery.length} images dans la galerie
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [password, setPassword] = useState(() => sessionStorage.getItem('admin_pw') || '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (password) {
      verifyPassword();
    }
  }, []);

  const verifyPassword = async () => {
    try {
      const res = await axios.post(`${API}/admin/verify`, { password });
      if (res.data.valid) {
        setIsAuthenticated(true);
      } else {
        sessionStorage.removeItem('admin_pw');
        setPassword('');
      }
    } catch {
      sessionStorage.removeItem('admin_pw');
      setPassword('');
    }
  };

  const handleLogin = (pw) => {
    sessionStorage.setItem('admin_pw', pw);
    setPassword(pw);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_pw');
    setPassword('');
    setIsAuthenticated(false);
  };

  if (!isAuthenticated) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return <Dashboard password={password} onLogout={handleLogout} />;
}
